import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { LocationIntegrations } from "@/types/EventTypes/Types/Location";

type LocationIds =
    | "no-location"
    | "google-meet"
    | "zoom"
    | "microsoft-teams"
    | "phone"
    | "in-person";

type LocationOption = {
    id: LocationIds;
    icon: string;
    name: string;
};

const locationTypeAliases: Record<string, LocationIds> = {
    google_meet: "google-meet",
    telephone: "phone",
    address: "in-person",
};

export type ResolvedLocation = {
    icon: string;
    label: string;
};

export function useLocationOptions(
    locationIntegrations?: LocationIntegrations,
) {
    const { t } = useI18n();

    const locationOptions = ref<LocationOption[]>([
        {
            id: "no-location",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-off"><path d="M5.43 5.43A8.06 8.06 0 0 0 4 10c0 6 8 12 8 12a29.94 29.94 0 0 0 5-5"/><path d="M19.18 13.52A8.66 8.66 0 0 0 20 10a8 8 0 0 0-8-8 7.88 7.88 0 0 0-3.52.82"/><path d="M9.13 9.13A2.78 2.78 0 0 0 9 10a3 3 0 0 0 3 3 2.78 2.78 0 0 0 .87-.13"/><path d="M14.9 9.25a3 3 0 0 0-2.15-2.16"/><line x1="2" x2="22" y1="2" y2="22"/></svg>`,
            name: t("event_types.location.no_location"),
        },
        {
            id: "google-meet",
            icon: `<svg viewBox="0 0 24 24"><g fill-rule="nonzero" fill="none"><path fill="#188038" d="M13.58 11.87l2.33 2.68 3.15 2 .55-4.66-.55-4.56-3.2 1.76z"></path><path d="M0 16.12v3.98c0 .91.74 1.65 1.65 1.65h3.97l.82-3.01-.82-2.62-2.73-.82-2.89.82z" fill="#1967D2"></path><path fill="#EA4335" d="M5.62 2L0 7.62l2.9.82 2.72-.82.81-2.58z"></path><path fill="#4285F4" d="M5.62 7.62H0v8.5h5.62z"></path><path d="M22.65 4.38l-3.59 2.95v9.23l3.61 2.96a.82.82 0 001.33-.65V5.02c0-.7-.8-1.08-1.35-.64zm-9.07 7.5v4.24H5.62v5.63h11.8c.9 0 1.64-.74 1.64-1.65v-3.54l-5.48-4.69z" fill="#34A853"></path><path d="M17.42 2H5.62v5.62h7.96v4.25l5.48-4.54V3.65c0-.91-.74-1.65-1.64-1.65z" fill="#FBBC04"></path></g></svg>`,
            name: "Google Meet",
        },
        {
            id: "zoom",
            icon: `<svg viewBox="0 0 24 24"><g fill="none" fill-rule="evenodd"><path fill="#2D8CFF" d="M6.55 0A6.55 6.55 0 000 6.55v10.9A6.55 6.55 0 006.55 24h10.9A6.55 6.55 0 0024 17.45V6.55A6.55 6.55 0 0017.45 0H6.55z"></path><path fill="#fff" d="M6 8a1 1 0 00-1 1v4a3 3 0 003 3h6a1 1 0 001-1v-4a3 3 0 00-3-3H6zM18.22 8.18L15.94 9.7a1 1 0 00-.44.83v2.93a1 1 0 00.44.83l2.28 1.55a.5.5 0 00.78-.41V8.59a.5.5 0 00-.09-.28.5.5 0 00-.41-.22.5.5 0 00-.28.09z"></path></g></svg>`,
            name: "Zoom",
        },
        {
            id: "microsoft-teams",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path fill="#7B83EB" d="M19 12.7h7.7c.7 0 1.3.6 1.3 1.3v7c0 2.7-2.2 4.9-4.9 4.9h0c-2.7 0-4.9-2.2-4.9-4.9v-7.7c-.1-.4.3-.7.7-.7-.1.1 0 .1.1 0z"/><circle fill="#7B83EB" cx="24.6" cy="9" r="3.2"/><circle fill="#5059C9" cx="14" cy="7.5" r="4.5"/><path fill="#5059C9" d="M19.7 12.7H7.6c-.7 0-1.3.6-1.3 1.3v7.7c0 4.3 3.5 7.8 7.8 7.8s7.8-3.5 7.8-7.8V14c-.1-.7-.7-1.3-1.4-1.3.1 0 0 0 0 0z"/><path fill="#FFF" d="M16.7 15.3h-1.9v5.3h-1.2v-5.3h-1.9v-1.1h5z"/></svg>`,
            name: "Microsoft Teams",
        },
        {
            id: "phone",
            icon: `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-phone"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
            name: t("event_types.location.phone_call"),
        },
        {
            id: "in-person",
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>`,
            name: t("event_types.location.in_person"),
        },
    ]);

    const locationsBasedOnIntegrations = computed(() => {
        return locationOptions.value.filter((option) => {
            if (option.id === "google-meet" && !locationIntegrations?.google) {
                return false;
            }

            if (option.id === "zoom" && !locationIntegrations?.zoom) {
                return false;
            }

            if (
                option.id === "microsoft-teams" &&
                !locationIntegrations?.microsoftTeams
            ) {
                return false;
            }

            return true;
        });
    });

    function getLocationForId(id: string) {
        return locationOptions.value.find((option) => option.id === id);
    }

    function resolveLocation(location: {
        type: string;
        value: string | null;
    }): ResolvedLocation | null {
        const id = locationTypeAliases[location.type] ?? location.type;
        const option = getLocationForId(id);
        if (!option) {
            return null;
        }

        const showCustomValue = id === "in-person" || id === "phone";
        return {
            icon: option.icon,
            label: (showCustomValue && location.value) || option.name,
        };
    }

    return {
        locationOptions,
        locationsBasedOnIntegrations,
        getLocationForId,
        resolveLocation,
    };
}
